
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { GameType } from "./GameCard";
import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";
import { Play, Info, Trophy } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";

interface GameModalProps {
  game: GameType;
  isOpen: boolean;
  onClose: () => void;
}

const GameModal: React.FC<GameModalProps> = ({ game, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState("play");
  const isMobile = useIsMobile();

  // Placeholder high scores for demo
  const highScores = [
    { name: "Player1", score: 1250 },
    { name: "Player2", score: 950 },
    { name: "Player3", score: 800 },
    { name: "Player4", score: 650 },
    { name: "Player5", score: 500 },
  ];

  const gameInstructions = {
    "memory-match": "Find matching pairs of cards by turning them over two at a time.",
    "simon-says": "Watch the sequence of colors and sounds, then repeat it in the same order.",
    "sequence-recall": "Memorize the sequence of numbers and recall them in the correct order.",
    "spot-difference": "Identify the differences between two similar images.",
    "stroop-test": "Name the color of the text, not what the text says.",
    "whack-a-mole": "Click on the moles as they appear before they disappear.",
    "sudoku": "Fill the grid so that every row, column, and 3×3 box contains the numbers 1-9.",
    "nonograms": "Use the numbers as clues to fill in cells and reveal a hidden picture.",
    "tower-hanoi": "Move the entire stack to another rod following specific rules.",
    "maze-solver": "Find the path from the entrance to the exit of the maze.",
    "quick-math": "Solve simple math problems as quickly as possible.",
    "number-sequences": "Identify the pattern and provide the next number in the sequence.",
    "word-search": "Find all the hidden words in a grid of letters.",
    "anagram-solver": "Rearrange the letters to form a valid word.",
    "word-chain": "Create a chain of words where the last letter of one word is the first letter of the next.",
    "dual-n-back": "Remember both position and sound from n steps back in the sequence.",
    "task-switching": "Rapidly switch between different tasks based on changing rules.",
    "reaction-test": "Test your reflexes by clicking colored targets as soon as they appear.",
    "pattern-memory": "Memorize a pattern of shapes or colors and recreate it from memory.",
    "n-back": "Remember and identify items that appeared N steps earlier in a sequence.",
    "word-pairs": "Study pairs of related words, then recall the matching word when shown one item.",
    "mental-rotation": "Mentally rotate shapes to determine if they match a target shape."
  };

  // Check if the game is available to play
  const availableGames = [
    "memory-match", "simon-says", "quick-math", "spot-difference", 
    "word-search", "stroop-test", "tower-hanoi", "whack-a-mole",
    "number-sequences", "anagram-solver", "maze-solver", "reaction-test",
    "pattern-memory", "n-back", "word-pairs", "mental-rotation"
  ];
  const isGameAvailable = availableGames.includes(game.id);

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog open={isOpen} onOpenChange={onClose}>
          <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
            >
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className="text-brain-purple">{game.icon}</span>
                  <span>{game.name}</span>
                </DialogTitle>
                <DialogDescription>
                  {game.description}
                </DialogDescription>
              </DialogHeader>
              
              <Tabs defaultValue="play" value={activeTab} onValueChange={setActiveTab} className="mt-4">
                <TabsList className="grid grid-cols-3 mb-4">
                  <TabsTrigger value="play" className="flex items-center gap-1">
                    <Play size={14} />
                    <span>Play</span>
                  </TabsTrigger>
                  <TabsTrigger value="info" className="flex items-center gap-1">
                    <Info size={14} />
                    <span>How to Play</span>
                  </TabsTrigger>
                  <TabsTrigger value="scores" className="flex items-center gap-1">
                    <Trophy size={14} />
                    <span>High Scores</span>
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="play" className="mt-0">
                  <div className="bg-muted p-4 md:p-6 rounded-md flex flex-col items-center justify-center text-center min-h-[200px] relative overflow-hidden">
                    <div className="absolute inset-0 opacity-5">
                      {/* Background pattern based on game type */}
                      {Array.from({ length: 10 }).map((_, i) => (
                        <motion.div
                          key={i}
                          className="absolute"
                          animate={{
                            x: [Math.random() * 100, Math.random() * 100],
                            y: [Math.random() * 100, Math.random() * 100],
                            opacity: [0.3, 0.8, 0.3],
                            scale: [1, 1.1, 1],
                          }}
                          transition={{
                            duration: Math.random() * 3 + 2,
                            repeat: Infinity,
                            repeatType: "reverse",
                          }}
                          style={{
                            left: `${Math.random() * 80}%`,
                            top: `${Math.random() * 80}%`,
                          }}
                        >
                          {game.icon}
                        </motion.div>
                      ))}
                    </div>
                    
                    {isGameAvailable ? (
                      <>
                        <p className="text-base font-medium mb-2 relative z-10">Ready to play?</p>
                        <p className="text-sm mb-6 relative z-10">{game.description}</p>
                        <Link to={`/games/${game.id}`} onClick={onClose}>
                          <Button 
                            size={isMobile ? "sm" : "lg"}
                            className="bg-brain-purple hover:bg-brain-vivid-purple text-white relative z-10"
                          >
                            Play Now
                          </Button>
                        </Link>
                      </>
                    ) : (
                      <>
                        <p className="text-muted-foreground mb-4 relative z-10">Game Coming Soon!</p>
                        <p className="text-sm mb-6 relative z-10">This game is currently in development. Check back later!</p>
                        <Button 
                          size={isMobile ? "sm" : "lg"}
                          className="bg-brain-purple hover:bg-brain-vivid-purple text-white relative z-10"
                          disabled
                        >
                          Play Demo
                        </Button>
                      </>
                    )}
                  </div>
                </TabsContent>
                
                <TabsContent value="info">
                  <div className="space-y-4 p-2">
                    <h4 className="font-medium">How to Play</h4>
                    <p className="text-sm text-muted-foreground">
                      {gameInstructions[game.id as keyof typeof gameInstructions] || 
                       "Learn the rules and strategies to master this game."}
                    </p>
                    
                    <h4 className="font-medium">Benefits</h4>
                    <ul className="text-sm text-muted-foreground list-disc pl-5 space-y-1">
                      <li>Improves cognitive processing speed</li>
                      <li>Enhances pattern recognition</li>
                      <li>Boosts working memory capacity</li>
                      <li>Increases focus and attention span</li>
                    </ul>
                  </div>
                </TabsContent>
                
                <TabsContent value="scores">
                  <div className="p-2">
                    <h4 className="font-medium mb-3">High Scores</h4>
                    <div className="space-y-2">
                      {highScores.map((score, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.1 }}
                          className={`flex justify-between p-2 rounded-md ${
                            index === 0 ? 'bg-brain-soft-purple/40' : 'bg-muted/50'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{index + 1}.</span>
                            <span>{score.name}</span>
                          </div>
                          <span className="font-bold">{score.score}</span>
                        </motion.div>
                      ))}
                    </div>
                    
                    <div className="mt-4 flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">Your Best: 425</p>
                      <Button variant="outline" size="sm">Reset Scores</Button>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
              
              <div className="flex justify-end mt-6">
                <Button onClick={onClose} variant="outline">Close</Button>
              </div>
            </motion.div>
          </DialogContent>
        </Dialog>
      )}
    </AnimatePresence>
  );
};

export default GameModal;
